import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** @type {EventTarget | null} */
  #listenerRoot = null;

  connectedCallback() {
    super.connectedCallback();
    // Listen on the section AND document so price still updates if DOM structure moves
    const closestSection = this.closest('.shopify-section, dialog');
    this.#listenerRoot = closestSection || document;
    this.#listenerRoot.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    // Fallback: document capture catches events even when price sits outside picker section
    if (this.#listenerRoot !== document) {
      document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listenerRoot?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Ignore events from other products when listening on document
    const eventProductId =
      event.target.closest('[data-product-id]')?.getAttribute('data-product-id') ||
      event.target.closest('variant-picker')?.dataset?.productId;
    if (eventProductId && this.dataset.productId && eventProductId !== this.dataset.productId) {
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        // Prefer matching block; fall back so price still updates if block id differs after morph
        const newProductPrice =
          html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`) ||
          html.querySelector(`product-price[data-product-id="${this.dataset.productId}"]`) ||
          html.querySelector('product-price');
        if (!newProductPrice) return;

        // Support Horizon default + custom-product-price (live theme markup)
        const priceContainer =
          this.querySelector('.custom-product-price') || this.querySelector('[ref="priceContainer"]');
        const volumePricingNote = this.querySelector('[ref="volumePricingNote"]');

        const newPrice =
          newProductPrice.querySelector('.custom-product-price') ||
          newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
        } else if (newPrice && !priceContainer) {
          this.prepend(newPrice);
        }

        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        const afterPrice =
          this.querySelector('.custom-product-price') || this.querySelector('[ref="priceContainer"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          afterPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
